/**
 * RecordFilterBar
 *
 * Status + group filters for the Records list. Status is a multi-select dropdown
 * (any subset of RecordStatus; empty = all). Group is a multi-select over the
 * entity's record groups (OR semantics). Both write to the URL via the callbacks
 * supplied by RecordsBrowser.
 */
import { FC, useMemo } from 'react';
import { Select } from 'antd';

import { RecordStatus } from '#types/System.Parameters.types';
import FlexCol from '../atoms/FlexCol';
import { EntityGroupMultiSelector } from '../artifacts/EntityGroupSelector';

interface RecordFilterBarProps {
    entityId: string;
    statusFilters: RecordStatus[];
    onStatusChange: (statuses: RecordStatus[]) => void;
    groupIds: string[];
    onGroupsChange: (groupIds: string[]) => void;
    disabled?: boolean;
}

const RecordFilterBar: FC<RecordFilterBarProps> = ({
    entityId,
    statusFilters,
    onStatusChange,
    groupIds,
    onGroupsChange,
    disabled = false,
}) => {
    const statusOptions = useMemo(
        () => Object.values(RecordStatus).map((status) => ({ label: status, value: status })),
        [],
    );

    const groupValue = useMemo(
        () => groupIds.map((entityGroupId) => ({ entityId, entityGroupId })),
        [groupIds, entityId],
    );

    return (
        <FlexCol noGrow style={{ width: '100%' }}>
            <EntityGroupMultiSelector
                entityId={entityId}
                value={groupValue}
                onChange={(selections) =>
                    onGroupsChange(
                        selections ? selections.map((s) => s.entityGroupId).filter((id): id is string => Boolean(id)) : [],
                    )
                }
                disabled={disabled}
                maxTagCount={5}
                style={{ width: '100%' }}
            />
            <Select
                mode="multiple"
                allowClear
                size="small"
                variant="filled"
                placeholder="All statuses"
                value={statusFilters}
                onChange={onStatusChange}
                options={statusOptions}
                disabled={disabled}
                aria-label="Filter by status"
                style={{ width: '100%' }}
            />
        </FlexCol>
    );
};

export default RecordFilterBar;
